import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Portal from "elements/Portal";
import { WEBSITE_BUILDER_PORTAL } from "../../../../WebsiteBuilderEditor";
import { websiteBuilderZLayers } from "../../../../websiteBuilderZLayers";
import { IWebsiteBuilderPopupContent, WebsiteBuilderPopup } from "./popup/WebsiteBuilderPopup";
import { TWebsiteBuilderTreeRefMap } from "../../../../hooks/treeRefMap/useTreeRefMapRegistration";

export interface IWebsiteBuilderContextMenuPosition {
  x: number;
  y: number;
}

export interface IWebsiteBuilderContextMenuOption {
  label: string;
  shortcut: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  stayOpenAfterClick?: boolean;
}

interface IWebsiteBuilderContextMenuProps {
  domRect?: DOMRect;
  showPopup: boolean;
  popupContent: IWebsiteBuilderPopupContent;
  position: IWebsiteBuilderContextMenuPosition;
  sections: IWebsiteBuilderContextMenuOption[][];
  treeRefMap: TWebsiteBuilderTreeRefMap;
  closeMenu: () => void;
}

type TWebsiteBuilderContextMenuOrientation = "none" | "top-left" | "top-right" | "bottom-left" | "bottom-right";

const MENU_WIDTH = 250;

export function WebsiteBuilderContextMenu(props: IWebsiteBuilderContextMenuProps) {
  const { domRect, showPopup, popupContent, position, sections, treeRefMap, closeMenu } = props;

  const menuRef = useRef<HTMLDivElement>(null);

  const [menuDomRect, setMenuDomRect] = useState<DOMRect>(null);
  const [menuOrientation, setMenuOrientation] = useState<TWebsiteBuilderContextMenuOrientation>("none");

  const handleKeydown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      closeMenu();
    }
  }, []);

  useEffect(() => {
    if (!showPopup) {
      document.addEventListener("keydown", handleKeydown);
    }

    return () => document.removeEventListener("keydown", handleKeydown);
  }, [showPopup, handleKeydown]);

  useEffect(() => {
    let resizeObserver: ResizeObserver = null;

    if (!menuRef.current) {
      return;
    }

    const update = () => {
      if (menuRef.current) {
        setMenuDomRect(menuRef.current.getBoundingClientRect());
      }
    };

    update();

    resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(menuRef.current);

    return () => resizeObserver?.disconnect();
  }, []);

  useEffect(() => {
    if (menuDomRect?.height == null) {
      return;
    }

    const OFFSET = 10;

    const xUpperBound = window.innerWidth - OFFSET;
    const exceededXUpperBound = position.x + MENU_WIDTH > xUpperBound;

    const yUpperBound = window.innerHeight - OFFSET;
    const exceededYUpperBound = position.y + menuDomRect.height > yUpperBound;

    let orientation: TWebsiteBuilderContextMenuOrientation = "none";

    if (exceededXUpperBound && exceededYUpperBound) {
      orientation = "bottom-right";
    } else if (exceededXUpperBound) {
      orientation = "top-right";
    } else if (exceededYUpperBound) {
      orientation = "bottom-left";
    } else {
      orientation = "top-left";
    }

    setMenuOrientation(orientation);
  }, [menuDomRect?.height, position]);

  const menuTransform: string = useMemo(() => {
    switch (menuOrientation) {
      case "top-right":
        return "translateX(-100%)";
      case "bottom-left":
        return "translateY(-100%)";
      case "bottom-right":
        return "translate(-100%, -100%)";
      default:
        return "";
    }
  }, [menuOrientation]);

  function renderMenuOption(menuOption: IWebsiteBuilderContextMenuOption) {
    return (
      <button
        className="context-menu-option"
        onClick={() => {
          menuOption.onClick();

          if (!menuOption.stayOpenAfterClick) {
            closeMenu();
          }
        }}
        disabled={menuOption.disabled}
      >
        <div className="context-menu-option-label">{menuOption.label}</div>
        <div className="context-menu-option-shortcut">{menuOption.shortcut}</div>
      </button>
    );
  }

  function renderMenuOptions(menuOptions: IWebsiteBuilderContextMenuOption[]) {
    return menuOptions.map((menuOption, index) => (
      <React.Fragment key={index}>{renderMenuOption(menuOption)}</React.Fragment>
    ));
  }

  return (
    <Portal isMounted={true} parentId={WEBSITE_BUILDER_PORTAL}>
      {!showPopup ? (
        <div
          ref={menuRef}
          className="website-builder-context-menu"
          style={{
            visibility: menuOrientation !== "none" ? "visible" : "hidden",
            transform: menuTransform,
            left: position.x,
            top: position.y,
            width: MENU_WIDTH,
            zIndex: websiteBuilderZLayers.portal,
          }}
          onMouseOver={e => e.stopPropagation()}
          onMouseOut={e => e.stopPropagation()}
          onDoubleClick={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
          onContextMenu={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {sections.map((section, index) => {
            return (
              <React.Fragment key={index}>
                {renderMenuOptions(section)}
                <hr className="context-menu-divider" />
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        <WebsiteBuilderPopup
          domRect={domRect ? domRect : menuDomRect}
          popupContent={popupContent}
          treeRefMap={treeRefMap}
          closePopup={closeMenu}
        />
      )}
    </Portal>
  );
}
